import type { CrudFormProps } from '@@partner:types:app'
import type { GQL_ID } from '@@partner:types:graphql'
import type { BrandManagerFormState } from './BrandManagersFormState'

import React, { useCallback, useEffect } from 'react'
import { mergeLeft, mergeLeftOnly } from 'merge-left-utils'
import { useFetchSingleBrandManager } from '@@partner:graphql/query/employees/singleBrandManager.gql'
import { useMutateSaveBrandManager } from '@@partner:graphql/mutation/employees/saveBrandManager.gql'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'
import {
  notifyOk,
  notifyError,
  makeGraphQLErrorNotification
} from '@@partner:helpers/notification'
import {
  makeBrandManagerFormState,
  mapApiDataToState,
  mapStateToApiVariable
} from './BrandManagersFormState'

import {
  Checkbox,
  Form,
  SelectPicker,
  Panel,
  Message,
  Grid,
  Row,
  Col
} from 'rsuite'
import CustomerPhone from '@components/order/form/CustomerPhone'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import { SaveButton } from '@components/common/form/Buttons'

import { BrandManagerFormValidationModel } from '../validation/BrandManagerFormValidationModel'

import { EMPLOYEE_STATUS_LABELS } from '@@partner:config/employee'
import FormCheckbox from '@components/common/form/FormCheckbox'

export interface BrandManagerFormProps extends CrudFormProps {
  id?: GQL_ID
}

export default function BrandManagerForm ({
  id,
  onSaved
}: BrandManagerFormProps) {
  const {
    data,
    loading: loadingBrandManager,
    error
  } = useFetchSingleBrandManager({ id })
  const [ saveBrandManager, { loading: loadingSaveBrandManager } ] =
    useMutateSaveBrandManager()

  const {
    formState,
    setFormState,
    setFormRef,
    handleFormChange,
    handleFormError
  } = useValidatableForm<BrandManagerFormState>(makeBrandManagerFormState)

  useEffect(() => {
    if (!loadingBrandManager && data) {
      setFormState((state) =>
        mergeLeft(state, mapApiDataToState(data?.brandManager))
      )
    }
  }, [ data, loadingBrandManager, setFormState ])

  const handleFormSubmit = useCallback(
    (isOk = false) => {
      if (isOk) {
        saveBrandManager({
          variables: {
            brandManagerInfo: mapStateToApiVariable(formState),
            upsert: !id
          }
        })
          .then(({ data }) => {
            notifyOk('Данные администратора сохранёны')
            setFormState((state) =>
              mergeLeftOnly([ 'id' ], state, data?.brandManager)
            )
            onSaved?.(data?.brandManager.id)
          })
          .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
      }
      else {
        notifyError('Форма заполнена некорректно')
      }
    },
    [ formState, id, onSaved, saveBrandManager, setFormState ]
  )

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        error={error}
        title="Ошибка загрузки данных менеджера"
      />
    )
  }

  return (
    <div className="BrandManagersForm">
      <Form
        model={BrandManagerFormValidationModel}
        formValue={formState}
        onChange={handleFormChange}
        onSubmit={handleFormSubmit}
        onCheck={handleFormError}
        ref={setFormRef}
        onError={handleFormError}
        layout="horizontal"
      >
        <h3 className="u-form-heading">Администратор</h3>

        <Grid>
          <Row>
            <Col md={12}>
              <Form.Group>
                <Form.ControlLabel>Фамилия</Form.ControlLabel>
                <Form.Control name="firstName" placeholder="Фамилия" />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>Имя</Form.ControlLabel>
                <Form.Control name="lastName" placeholder="Имя" />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>E-mail</Form.ControlLabel>
                <Form.Control name="email" placeholder="E-mail" />
              </Form.Group>
              {!id ? (
                <Form.Group>
                  <Form.ControlLabel>Пароль</Form.ControlLabel>
                  <Form.Control
                    name="password"
                    placeholder="Пароль"
                    type="password"
                  />
                </Form.Group>
              ) : null}
              <Form.Group>
                <CustomerPhone name="phone" label="Телефон" />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>Статус</Form.ControlLabel>
                <Form.Control
                  name="status"
                  accepter={SelectPicker}
                  data={EMPLOYEE_STATUS_LABELS}
                  searchable={false}
                  cleanable={false}
                />
              </Form.Group>
            </Col>

            {!!id ? (
              <Col md={12}>
                <Message
                  className="p-4 max-w-lg"
                  type="error"
                  header="Опасные действия"
                  showIcon
                >
                  <Form.Group>
                    <Form.ControlLabel>Изменить пароль</Form.ControlLabel>
                    <FormCheckbox.Control
                      name="changePassword"
                      placeholder="Изменить пароль"
                    />
                    {formState.changePassword ? (
                      <>
                        <Form.Control
                          className="my-1"
                          type="password"
                          name="password"
                          placeholder="Пароль"
                        />
                        <Form.Control
                          className="my-1"
                          type="password"
                          name="repeatPassword"
                          placeholder="Повторите"
                        />
                      </>
                    ) : null}
                  </Form.Group>
                </Message>
              </Col>
            ) : null}
          </Row>
        </Grid>

        <SaveButton
          loading={loadingBrandManager || loadingSaveBrandManager}
          disabled={loadingBrandManager || loadingSaveBrandManager}
        >
          Сохранить
        </SaveButton>
      </Form>
    </div>
  )
}
